import { useState } from "react";
import { useForm } from "react-hook-form";
import {
  Globe,
  Activity,
  Zap,
  Plus,
  Pencil,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import {
  useCreateServer,
  useDeleteServer,
  useServers,
  useServerLoad,
  useUpdateServer,
} from "@/api/queries";
import { getCurrentPrincipal } from "@/api/client";
import type { ServerCreate, ServerView } from "@/api/types";
import { Card, CardBody } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input, Label } from "@/components/ui/input";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";

const ROLE_LABEL: Record<string, string> = {
  main: "Основной",
  relay: "Релей",
  cdn: "CDN",
  reserve: "Резерв",
  landing: "Лендинг",
};

export function ServersPage() {
  const principal = getCurrentPrincipal();
  const { data: servers, isLoading } = useServers();
  const { data: load } = useServerLoad();

  const canManage = principal?.is_superadmin ?? false;

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ServerView | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ServerView | null>(null);

  const loadBySlug = new Map(load?.map((l) => [l.slug, l]) ?? []);

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <span className="label">Инфраструктура</span>
          <h1 className="font-display text-3xl font-bold mt-2">Серверы</h1>
          <p className="text-sm text-fg-muted mt-1">
            Каталог серверов, из которых раздаётся VPN. Обновление нагрузки — раз в 30 секунд.
          </p>
        </div>
        {canManage && (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus size={16} /> Добавить сервер
          </Button>
        )}
      </header>

      <Card>
        <CardBody className="p-0">
          <Table>
            <THead>
              <TR>
                <TH>Slug</TH>
                <TH>Роль</TH>
                <TH>IP / Host</TH>
                <TH>Пул</TH>
                <TH>
                  <span className="inline-flex items-center gap-1">
                    <Activity size={12} /> Пользователей
                  </span>
                </TH>
                <TH>
                  <span className="inline-flex items-center gap-1">
                    <Zap size={12} /> Нагрузка
                  </span>
                </TH>
                {canManage && <TH className="text-right">Действия</TH>}
              </TR>
            </THead>
            <TBody>
              {isLoading && (
                <TR>
                  <TD colSpan={canManage ? 7 : 6} className="text-center text-fg-subtle py-8">
                    Загрузка…
                  </TD>
                </TR>
              )}
              {servers?.map((s) => {
                const l = loadBySlug.get(s.slug);
                const scorePct = l ? Math.round(l.score * 100) : null;
                return (
                  <TR key={s.id}>
                    <TD className="font-mono text-sm flex items-center gap-2">
                      <Globe size={12} className="text-fg-subtle" />
                      {s.slug}
                    </TD>
                    <TD>
                      <Badge
                        tone={
                          s.role === "main"
                            ? "accent"
                            : s.role === "relay"
                              ? "info"
                              : "neutral"
                        }
                      >
                        {ROLE_LABEL[s.role] ?? s.role}
                      </Badge>
                    </TD>
                    <TD className="font-mono text-xs text-fg-muted">
                      <div>{s.ip}</div>
                      <div className="text-fg-subtle">{s.host}</div>
                    </TD>
                    <TD>
                      {s.enabled_for_pool ? (
                        <Badge tone="success">активен</Badge>
                      ) : (
                        <Badge tone="warning">резерв</Badge>
                      )}
                    </TD>
                    <TD>{l?.active_clients ?? "—"}</TD>
                    <TD>
                      {scorePct !== null ? (
                        <span
                          className={
                            scorePct > 75
                              ? "text-state-danger"
                              : scorePct > 40
                                ? "text-state-warning"
                                : "text-state-success"
                          }
                        >
                          {scorePct}%
                        </span>
                      ) : (
                        <span className="text-fg-subtle">—</span>
                      )}
                    </TD>
                    {canManage && (
                      <TD className="text-right space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditTarget(s)}
                          title="Изменить"
                        >
                          <Pencil size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteTarget(s)}
                          title="Удалить"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </TD>
                    )}
                  </TR>
                );
              })}
            </TBody>
          </Table>
        </CardBody>
      </Card>

      <div className="text-xs text-fg-subtle">
        Нагрузка = 0.4 × active_clients + 0.3 × CPU + 0.3 × исходящий трафик.
        Влияет на распределение новых пользователей между серверами.
      </div>

      <CreateServerDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      <EditServerDialog server={editTarget} onClose={() => setEditTarget(null)} />
      <DeleteServerDialog
        server={deleteTarget}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}

// ─── Create ───────────────────────────────────────────────────────────

function CreateServerDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { register, handleSubmit, reset, formState } = useForm<
    ServerCreate & { xui_inbound_ids_str: string }
  >({
    defaultValues: {
      role: "reserve",
      enabled_for_pool: false,
      xui_inbound_ids_str: "",
    },
  });
  const create = useCreateServer();
  const [err, setErr] = useState<string | null>(null);

  const submit = async (d: ServerCreate & { xui_inbound_ids_str: string }) => {
    setErr(null);
    const ids = d.xui_inbound_ids_str
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => parseInt(s, 10))
      .filter((n) => !Number.isNaN(n));
    try {
      await create.mutateAsync({
        slug: d.slug.toLowerCase().trim(),
        host: d.host.trim(),
        ip: d.ip.trim(),
        role: d.role,
        sni: d.sni?.trim() || null,
        public_key: d.public_key?.trim() || null,
        xui_inbound_ids: ids,
        enabled_for_pool: d.enabled_for_pool ?? false,
      });
      reset();
      onClose();
    } catch (e: unknown) {
      setErr(
        (e as { response?: { data?: { detail?: string } } }).response?.data
          ?.detail ?? "Ошибка",
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Новый сервер"
      description="Сервер должен быть физически провиженен заранее (x-ui, Reality-ключи, inbound'ы)."
      className="max-w-xl"
    >
      <form onSubmit={handleSubmit(submit)} className="space-y-3">
        <div className="text-sm text-state-warning bg-state-warning/10 border border-state-warning/20 rounded-lg p-3 flex gap-2">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div>
            Добавление сервера в БД <b>не провизионит</b> x-ui. Убедитесь, что
            x-ui уже установлен и inbound'ы совпадают с теми, что вы указываете
            ниже.
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="slug">Slug</Label>
            <Input
              id="slug"
              placeholder="spb-relay-2"
              pattern="[a-z0-9][a-z0-9-]*[a-z0-9]"
              {...register("slug", { required: true })}
            />
          </div>
          <div>
            <Label htmlFor="role">Роль</Label>
            <select
              id="role"
              className="w-full h-10 px-3 text-sm bg-bg-elevated border border-border rounded-xl text-fg"
              {...register("role", { required: true })}
            >
              <option value="main">Основной</option>
              <option value="relay">Релей</option>
              <option value="cdn">CDN</option>
              <option value="reserve">Резерв</option>
              <option value="landing">Лендинг</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="ip">IP</Label>
            <Input
              id="ip"
              placeholder="192.0.2.1"
              {...register("ip", { required: true })}
            />
          </div>
          <div>
            <Label htmlFor="host">Host (FQDN)</Label>
            <Input
              id="host"
              placeholder="spb2.example.com"
              {...register("host", { required: true })}
            />
          </div>
        </div>

        <div>
          <Label htmlFor="sni">SNI (для Reality маскировки)</Label>
          <Input
            id="sni"
            placeholder="www.microsoft.com"
            {...register("sni")}
          />
        </div>

        <div>
          <Label htmlFor="public_key">Reality public key</Label>
          <Input
            id="public_key"
            placeholder="4FUeUEUfpzGHNE3WVAmtiCVRCWpMOKpia6NWV3noVCU"
            {...register("public_key")}
          />
        </div>

        <div>
          <Label htmlFor="xui_inbound_ids_str">X-UI inbound IDs (через запятую)</Label>
          <Input
            id="xui_inbound_ids_str"
            placeholder="2, 3"
            {...register("xui_inbound_ids_str")}
          />
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" {...register("enabled_for_pool")} />
          <span>Включить в pool раздачи подписок</span>
        </label>

        {err && <div className="text-sm text-state-danger">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" disabled={formState.isSubmitting}>
            Создать
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ─── Edit ─────────────────────────────────────────────────────────────

function EditServerDialog({
  server,
  onClose,
}: {
  server: ServerView | null;
  onClose: () => void;
}) {
  const { register, handleSubmit, formState } = useForm<{
    host: string;
    ip: string;
    sni: string;
    public_key: string;
    xui_inbound_ids_str: string;
    enabled_for_pool: boolean;
  }>({
    values: server
      ? {
          host: server.host,
          ip: server.ip,
          sni: server.sni ?? "",
          public_key: "",
          xui_inbound_ids_str: (server.xui_inbound_ids ?? []).join(", "),
          enabled_for_pool: server.enabled_for_pool,
        }
      : undefined,
  });
  const update = useUpdateServer();
  const [err, setErr] = useState<string | null>(null);

  if (!server) return null;

  const submit = async (d: {
    host: string;
    ip: string;
    sni: string;
    public_key: string;
    xui_inbound_ids_str: string;
    enabled_for_pool: boolean;
  }) => {
    setErr(null);
    const ids = d.xui_inbound_ids_str
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => parseInt(s, 10))
      .filter((n) => !Number.isNaN(n));
    try {
      await update.mutateAsync({
        id: server.id,
        body: {
          host: d.host.trim(),
          ip: d.ip.trim(),
          sni: d.sni.trim() || null,
          public_key: d.public_key.trim() || undefined,
          xui_inbound_ids: ids,
          enabled_for_pool: d.enabled_for_pool,
        },
      });
      onClose();
    } catch (e: unknown) {
      setErr(
        (e as { response?: { data?: { detail?: string } } }).response?.data
          ?.detail ?? "Ошибка",
      );
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Изменить сервер ${server.slug}`}
      className="max-w-xl"
    >
      <form onSubmit={handleSubmit(submit)} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="e-ip">IP</Label>
            <Input id="e-ip" {...register("ip")} />
          </div>
          <div>
            <Label htmlFor="e-host">Host</Label>
            <Input id="e-host" {...register("host")} />
          </div>
        </div>
        <div>
          <Label htmlFor="e-sni">SNI</Label>
          <Input id="e-sni" {...register("sni")} />
        </div>
        <div>
          <Label htmlFor="e-pk">Новый Reality public key (оставьте пустым чтобы не менять)</Label>
          <Input id="e-pk" {...register("public_key")} />
        </div>
        <div>
          <Label htmlFor="e-inb">X-UI inbound IDs (через запятую)</Label>
          <Input id="e-inb" {...register("xui_inbound_ids_str")} />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" {...register("enabled_for_pool")} />
          <span>Активен в pool</span>
        </label>
        {err && <div className="text-sm text-state-danger">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" disabled={formState.isSubmitting}>
            Сохранить
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ─── Delete ───────────────────────────────────────────────────────────

function DeleteServerDialog({
  server,
  onClose,
}: {
  server: ServerView | null;
  onClose: () => void;
}) {
  const del = useDeleteServer();
  const [err, setErr] = useState<string | null>(null);
  if (!server) return null;

  const submit = async () => {
    setErr(null);
    try {
      await del.mutateAsync(server.id);
      onClose();
    } catch (e: unknown) {
      setErr(
        (e as { response?: { data?: { detail?: string } } }).response?.data
          ?.detail ?? "Ошибка",
      );
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Удалить сервер ${server.slug}?`}
      description="Нельзя удалить сервер, на который ссылаются активные подписки."
    >
      <div className="space-y-3">
        <div className="text-sm text-state-warning bg-state-warning/10 border border-state-warning/20 rounded-lg p-3">
          Удаление не деинсталлирует x-ui на самом VPS. Для Freedom-пользователей
          этот сервер продолжит раздавать VPN — Net просто перестанет добавлять
          на него новых клиентов.
        </div>
        {err && <div className="text-sm text-state-danger">{err}</div>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="danger" onClick={submit} disabled={del.isPending}>
            Удалить
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
